

// src/components/TicketDetail.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Clock,
  ChevronLeft,
  Edit,
  Trash,
  MessageCircle,
  PaperclipIcon,
  CheckCircle,
  Clock as ClockIcon,
  AlertCircle,
  XCircle,
} from "lucide-react";
import TicketService from "../../../services/TicketService";
import CommentService from "../../../services/CommentService";

const TicketDetail = () => {
  
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { ticketId } = useParams();
  const navigate = useNavigate();

  // State for comment dialog
  const [openCommentDialog, setOpenCommentDialog] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentFiles, setCommentFiles] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        setLoading(true);
        const data = await TicketService.getTicketById(ticketId);
        setTicket(data);
        console.log("Ticket Data: ", data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching ticket:", err);
        setError("Failed to load ticket details. Please try again.");
        setLoading(false);
      }
    };

    fetchTicket();
  }, [ticketId]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "HIGH":
        return "bg-red-100 text-red-800";
      case "MEDIUM":
        return "bg-yellow-100 text-yellow-800";
      case "LOW":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityDot = (priority) => {
    switch (priority) {
      case "HIGH":
        return "bg-red-500";
      case "MEDIUM":
        return "bg-yellow-500";
      case "LOW":
        return "bg-green-500";
      default:
        return "bg-gray-500";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "OPEN":
      case "NEW":
        return "bg-blue-100 text-blue-800";
      case "IN_PROGRESS":
        return "bg-yellow-100 text-yellow-800";
      case "RESOLVED":
      case "COMPLETED":
        return "bg-green-100 text-green-800";
      case "CLOSED":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "OPEN":
      case "NEW":
        return <AlertCircle size={14} className="mr-1" />;
      case "IN_PROGRESS":
        return <ClockIcon size={14} className="mr-1" />;
      case "RESOLVED":
      case "COMPLETED":
        return <CheckCircle size={14} className="mr-1" />;
      case "CLOSED":
        return <XCircle size={14} className="mr-1" />;
      default:
        return null;
    }
  };

  const formatStatusText = (status) => {
    return status.replace(/_/g, " ");
  };

  // Comment dialog handlers
  const handleOpenCommentDialog = () => {
    setOpenCommentDialog(true);
    setCommentText("");
    setCommentFiles([]);
    setSuccessMessage("");
    setError(null);
  };

  const handleCloseCommentDialog = () => {
    setOpenCommentDialog(false);
  };

  const handleCommentChange = (e) => {
    setCommentText(e.target.value);
  };

  const handleCommentFileChange = (e) => {
    if (e.target.files) {
      setCommentFiles(Array.from(e.target.files));
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) {
      setError("Comment text is required.");
      return;
    }

    try {
      setSubmitLoading(true);

      // Create comment data
      const commentData = {
        text: commentText,
        ticketId: ticketId,
      };

      // Add the comment with files if any
      await CommentService.createComment(commentData, commentFiles);

      // Refresh ticket data to show new comment
      const updatedTicket = await TicketService.getTicketById(ticketId);
      setTicket(updatedTicket);

      // Show success message
      setSuccessMessage("Comment added successfully!");

      // Close dialog after a short delay
      setTimeout(() => {
        handleCloseCommentDialog();
        setSuccessMessage("");
      }, 2000);

      setSubmitLoading(false);
    } catch (err) {
      console.error("Error adding comment:", err);
      setError("Failed to add comment. Please try again.");
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="bg-red-100 text-red-700 p-4 rounded-md my-4" role="alert">
        <p className="font-bold">Error</p>
        <p>{error || "Ticket not found"}</p>
        <button
          onClick={() => navigate("/dashboard/ticket")}
          className="mt-4 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
        >
          Back to Tickets
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      {/* Success message */}
      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded m-4">
          {successMessage}
        </div>
      )}

      {/* Header with back button and actions */}
      <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
        <div className="flex items-center">
          <button
            onClick={() => navigate("/dashboard/ticket")}
            className="mr-4 text-gray-500 hover:text-gray-700"
          >
            <ChevronLeft size={20} />
          </button>
          <h1 className="text-xl font-semibold text-gray-800">
            Ticket Details
          </h1>
        </div>
        {/* <div className="flex space-x-2">
          <button className="p-2 text-blue-600 hover:text-blue-800 rounded-full hover:bg-blue-50">
            <Edit size={18} />
          </button>
          <button className="p-2 text-red-600 hover:text-red-800 rounded-full hover:bg-red-50">
            <Trash size={18} />
          </button>
        </div> */}
      </div>

      {/* Ticket main content */}
      <div className="p-6">
        {/* Ticket header */}
        <div className="flex items-center justify-between mb-4">
          <span className="inline-flex items-center text-sm font-medium text-gray-600">
            #{ticket.kkgtTicketId}
          </span>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(
              ticket.priority
            )}`}
          >
            {ticket.priority}
          </span>
        </div>

        {/* Ticket title and description */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {ticket.title}
          </h2>
          <p className="text-gray-600">{ticket.description}</p>
        </div>

        {/* Ticket metadata */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="flex items-center">
            <div className="w-32 text-sm font-medium text-gray-500">
              Status:
            </div>
            <div
              className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${getStatusColor(
                ticket.status
              )}`}
            >
              {getStatusIcon(ticket.status)}
              <span>{formatStatusText(ticket.status)}</span>
            </div>
          </div>

          <div className="flex items-center">
            <div className="w-32 text-sm font-medium text-gray-500">
              Project:
            </div>
            <div className="flex items-center">
              <span
                className={`w-2 h-2 rounded-full ${getPriorityDot(
                  ticket.priority
                )}`}
              ></span>
              <span className="ml-1.5 text-sm text-gray-700">
                {ticket.project.name}
              </span>
            </div>
          </div>

          <div className="flex items-center">
            <div className="w-32 text-sm font-medium text-gray-500">
              Created By:
            </div>
            <div className="flex items-center">
              {ticket.createdBy && (
                <>
                  <span className="text-sm text-gray-700">
                    {ticket.createdBy.name}
                  </span>
                  <span className="ml-1.5 text-xs text-gray-500">
                    ({ticket.createdBy.email})
                  </span>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center">
            <div className="w-32 text-sm font-medium text-gray-500">
              Assigned To:
            </div>
            <div className="flex items-center">
              {ticket.assignedTo && ticket.assignedTo.length > 0 ? (
                ticket.assignedTo.map((user, index) => (
                  <div key={index} className="flex items-center mr-2">
                    <img
                      className="w-6 h-6 rounded-full mr-1"
                      src={`https://ui-avatars.com/api/?name=${
                        user.name || "User"
                      }&background=random`}
                      alt={user.name || "User"}
                    />
                    <span className="text-sm text-gray-700">{user.name}</span>
                  </div>
                ))
              ) : (
                <span className="text-sm text-gray-500 italic">
                  Not assigned
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center">
            <div className="w-32 text-sm font-medium text-gray-500">
              Updated:
            </div>
            <div className="flex items-center text-sm text-gray-700">
              <Clock size={14} className="mr-1.5" />
              <span>
                {new Date(ticket.updatedAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </span>
            </div>
          </div>

          <div className="flex items-center">
            <div className="w-32 text-sm font-medium text-gray-500">
              Created:
            </div>
            <div className="flex items-center text-sm text-gray-700">
              <Clock size={14} className="mr-1.5" />
              <span>
                {new Date(ticket.createdAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Attachments section */}
        {ticket.attachment && ticket.attachment.length > 0 && (
          <div className="mb-6">
            <h3 className="text-md font-semibold text-gray-800 mb-2">
              Attachments
            </h3>
            <div className="space-y-2">
              {ticket.attachment.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center p-2 border rounded-md"
                >
                  <PaperclipIcon size={16} className="mr-2 text-gray-500" />
                  <a
                    href={file.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline cursor-pointer flex items-center"
                  >
                    {file.fileName}
                    {file.fileSize && (
                      <span className="text-xs text-gray-500 ml-2">
                        ({Math.round(file.fileSize / 1024)} KB)
                      </span>
                    )}
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Comments section */}
        <div>
          <h3 className="text-md font-semibold text-gray-800 mb-2">Comments</h3>
          {ticket.comment && ticket.comment.length > 0 ? (
            <div className="space-y-4">
              {ticket.comment.map((comment, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-md">
                  <div className="flex items-center mb-2">
                    <img
                      className="w-6 h-6 rounded-full mr-2"
                      src={`https://ui-avatars.com/api/?name=${
                        comment.createdBy?.name || "User"
                      }&background=random`}
                      alt="Avatar"
                    />
                    <span className="text-sm font-medium text-gray-800">
                      {comment.createdBy?.name || "Unknown User"}
                    </span>
                    <span className="text-xs text-gray-500 ml-2">
                      {new Date(comment.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{comment.text}</p>

                  {/* Comment attachments if any */}
                  {comment.attachment && comment.attachment.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs font-medium text-gray-500 mb-1">
                        Attachments:
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {comment.attachment.map((file, fileIndex) => (
                          <a
                            key={fileIndex}
                            href={file.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs bg-gray-100 text-blue-700 px-2 py-1 rounded flex items-center hover:bg-gray-200"
                          >
                            <PaperclipIcon size={12} className="mr-1" />
                            {file.fileName}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500 italic mb-4">
              No comments yet
            </div>
          )}
        </div>
      </div>

      {/* Footer with action buttons */}
      <div className="bg-gray-50 px-6 py-4 border-t flex justify-between">
        {/* <button
          onClick={() => navigate("/dashboard/ticket")}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
        >
          Back to List
        </button> */}
        <div></div>
        <div className="flex space-x-2">
          <button
            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 flex items-center"
            onClick={handleOpenCommentDialog}
          >
            <MessageCircle size={16} className="mr-1" />
            Add Comment
          </button>
        </div>
      </div>

      {/* Add Comment Dialog */}
      {openCommentDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Add Comment</h3>
            </div>

            <div className="p-6">
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comment *
                </label>
                <textarea
                  rows="4"
                  value={commentText}
                  onChange={handleCommentChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your comment here..."
                  required
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Attachments (Optional)
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="comment-file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                      >
                        <span>Upload files</span>
                        <input
                          id="comment-file-upload"
                          name="comment-file-upload"
                          type="file"
                          className="sr-only"
                          multiple
                          onChange={handleCommentFileChange}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, PDF up to 10MB
                    </p>
                  </div>
                </div>

                {commentFiles.length > 0 && (
                  <div className="mt-3">
                    <h4 className="text-sm font-medium text-gray-700">
                      Selected Files:
                    </h4>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {commentFiles.map((file, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full"
                        >
                          {file.name}
                          <button
                            type="button"
                            className="ml-1 text-gray-500 hover:text-gray-700"
                            onClick={() => {
                              const newFiles = [...commentFiles];
                              newFiles.splice(index, 1);
                              setCommentFiles(newFiles);
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={handleCloseCommentDialog}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium text-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  submitLoading || !commentText.trim()
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
                onClick={handleAddComment}
                disabled={submitLoading || !commentText.trim()}
              >
                {submitLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </div>
                ) : (
                  "Add Comment"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetail;
